import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Servicio para manejar operaciones de Retención de Honorarios
 * Diseñado para ser usado por agentes autónomos vía MCP
 *
 * Este servicio encapsula todas las llamadas al API relacionadas con
 * la gestión de boletas de honorarios de profesionales terceros.
 */
class RetencionHonorariosService {
    /**
     * Configurar headers de autenticación
     */
    _getHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    /**
     * Manejar errores de API
     */
    _handleError(error, defaultMessage) {
        if (error.response) {
            throw new Error(error.response.data.error || error.response.data.message || defaultMessage);
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error(error.message || defaultMessage);
        }
    }

    /**
     * Obtener retenciones de honorarios con filtros opcionales
     *
     * @param {Object} params - Parámetros de filtro
     * @param {number} params.id_company - ID de la empresa (requerido)
     * @param {number} params.anio - Año para filtrar (opcional)
     * @param {number} params.mes - Mes para filtrar (opcional)
     * @param {string} params.estado - Estado (pendiente, pagado, anulado) (opcional)
     * @param {string} params.rut_profesional - RUT del profesional (opcional)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de retenciones de honorarios
     */
    async getRetencionesHonorarios(params, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios`,
                {
                    params,
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener retenciones de honorarios');
        }
    }

    /**
     * Obtener una retención de honorarios por ID
     *
     * @param {number} id - ID de la retención
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Datos de la retención
     */
    async getRetencionHonorarioById(id, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener retención de honorarios');
        }
    }

    /**
     * Crear una nueva retención de honorarios
     *
     * @param {Object} retencionData - Datos de la retención
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Retención creada
     */
    async createRetencionHonorario(retencionData, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios`,
                retencionData,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al crear retención de honorarios');
        }
    }

    /**
     * Eliminar una retención de honorarios
     *
     * @param {number} id - ID de la retención
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la operación
     */
    async deleteRetencionHonorario(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar retención de honorarios');
        }
    }

    /**
     * Obtener resumen de retenciones por periodo
     *
     * @param {number} anio - Año para el resumen
     * @param {number|null} mes - Mes para el resumen (opcional)
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resumen de retenciones
     */
    async getResumen(anio, mes, id_company, token) {
        try {
            const url = mes
                ? `${API_BASE_URL}/api/rrhh/retencion-honorarios/resumen/${anio}/${mes}`
                : `${API_BASE_URL}/api/rrhh/retencion-honorarios/resumen/${anio}`;

            const response = await axios.get(
                url,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener resumen de retenciones');
        }
    }

    /**
     * Obtener lista de profesionales únicos
     *
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de profesionales
     */
    async getProfesionales(id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/profesionales`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener profesionales');
        }
    }

    /**
     * Calcular retención de honorarios
     *
     * @param {number} monto_bruto - Monto bruto de la boleta
     * @param {number} tasa_retencion - Tasa de retención (por defecto 11.5%)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Cálculo de retención
     */
    async calcularRetencion(monto_bruto, tasa_retencion, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/calcular`,
                { monto_bruto, tasa_retencion },
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al calcular retención');
        }
    }

    /**
     * Crear asientos contables para retenciones de honorarios
     *
     * @param {Object} data - Datos para crear asientos
     * @param {number} data.id_company - ID de la empresa
     * @param {Array} data.ids_retenciones - IDs de retenciones específicas (opcional)
     * @param {number} data.mes - Mes del período (opcional si se envían ids_retenciones)
     * @param {number} data.anio - Año del período (opcional si se envían ids_retenciones)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la creación de asientos
     */
    async crearAsientosContables(data, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/asientos`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear asientos contables');
        }
    }

    /**
     * Cargar honorarios mensuales desde archivo Excel
     *
     * @param {FormData} formData - FormData con el archivo Excel, id_company, mes y anio
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la carga
     */
    async uploadHonorariosMensuales(formData, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/upload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al cargar archivo de honorarios mensuales');
        }
    }

    /**
     * Obtener estado de carga de los 12 meses para un año
     *
     * @param {number} id_company - ID de la empresa
     * @param {number} anio - Año
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Array de 12 meses con cargado/procesado
     */
    async getEstadoMeses(id_company, anio, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/estado-meses`,
                { params: { id_company, anio }, headers: this._getHeaders(token) }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener estado de meses');
        }
    }

    /**
     * Consultar honorarios en el SII via Rasca
     *
     * @param {number} id_company - ID de la empresa
     * @param {number} year - Año
     * @param {number} month - Mes (1-12)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la consulta
     */
    async consultarSiiHonorarios(id_company, year, month, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/retencion-honorarios/consultar-sii`,
                { id_company, year, month },
                { headers: this._getHeaders(token), timeout: 120000 }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al consultar honorarios en el SII');
        }
    }
}

// Exportar instancia única del servicio (patrón Singleton)
const retencionHonorariosService = new RetencionHonorariosService();
export default retencionHonorariosService;
